import fetcher from '../../helpers/fetcher';
import { ENVIRONMENT_CONFIG } from '../../constants/environmentConfig';
import { unwrapDataResponse } from '../../utils/unwrapDataResponse';
import { API_ENDPOINTS } from '../../constants/apiEndpoints';

export interface AlertListQuery {
  page?: number;
  limit?: number;
  isRead?: boolean;
  isActive?: boolean;
  productRefIds?: string[];
  alertConfigId?: string;
  basketIds?: number[];
  dateRange?: [number, number];
  metric?: string;
}

export interface AlertData {
  id: number;
  alertConfigId: number;
  productId: number;
  priceId: number | null;
  triggeredAt: number;
  instanceCount: number;
  alertDetails: string | null;
  isActive: boolean;
  isRead: boolean;
  notes: string | null;
  productName: string;
  productPresentation: string;
  alertConfigName: string;
  basketName: string;
  lastReportedPrice: number;
  currency: string;
  priceReportedTime: number;
  location: string;
  alertThreshold?: string;
  alertThresholdValue?: number;
  alertCondition?: string;
}

export interface AlertListResponse {
  data: AlertData[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface PerformanceQuery {
  startDate: number;
  endDate: number;
  userId?: number;
  page?: number;
  limit?: number;
}

export interface PerformanceData {
  tasks: {
    assigned: number;
    completed: number;
    completionRate: number;
  };
  products: {
    assigned: number;
    monitored: number;
    monitoringRate: number;
  };
  establishments: {
    assigned: number;
    visited: number;
    visitRate: number;
  };
  period: {
    startDate: number;
    endDate: number;
  };
}

export interface DetailedPerformanceData {
  tasks: {
    assigned: number;
    completed: number;
    completionRate: number;
    taskList: Array<{
      refId: string;
      taskName: string;
      status: string;
      isCompleted: boolean;
      assigneeNames?: string;
    }>;
  };
  products: {
    assigned: number;
    monitored: number;
    monitoringRate: number;
    productList: Array<{
      refId: string;
      name: string;
      isMonitored: boolean;
      taskName?: string;
      lastPrice?: number;
    }>;
  };
  establishments: {
    assigned: number;
    visited: number;
    visitRate: number;
    establishmentList: Array<{
      id: number;
      name: string;
      isVisited: boolean;
      locationName?: string;
    }>;
  };
  period: {
    startDate: number;
    endDate: number;
  };
}

export interface ExternalDataUpload {
  fileContent: string;
  type: string;
}

export interface ExternalDataTemplateDownload {
  type: string;
}

export interface IHMAPricingData {
  productId: number;
  startDate: string;
  endDate: string;
  unit: string;
  amountPerUnit: number;
  strategicReserveUnit: string;
  strategicReserveAmount: number;
  productiveCycle: string;
}

export interface IhmaPriceBreakdownParams {
  productId: number;
  startMonthEpoch?: number;
  endMonthEpoch?: number;
  timezoneOffset?: number;
}

export interface IhmaPriceBreakdownResponse {
  categories: string[];
  series: Array<{
    name: string;
    data: number[];
    color?: string;
  }>;
  timeInterval: string;
  latestIhmaPrice?: number;
  latestReserveAmount?: number;
}

export interface BCHOverlayParams {
  type: string;
  mode?: string;
  year?: number;
  division?: string;
  region?: string;
  startTime: number;
  endTime: number;
  timezoneOffset: number;
}

export interface UtilityConfigPresentation {
  presentation: string;
  productionCost: number;
  otherCosts?: number;
  stage1Price: number;
  stage2Price: number;
  stage3Price: number;
}

export interface SaveUtilityConfigPayload {
  productRefId: string;
  presentations: UtilityConfigPresentation[];
}

class AnalysisService {
  private readonly baseUrl = ENVIRONMENT_CONFIG.ANALYSIS_SERVICE;

  private async resolveBasketParams(
    params: any,
    queryParams: Record<string, string | number[]>
  ) {
    if (!params || !queryParams) return;

    if (params?.basketId != null) {
      queryParams.basketId = params.basketId.toString();
      return;
    }
    if (params?.basketIds && Array.isArray(params.basketIds) && params.basketIds.length > 0) {
      queryParams.basketIds = params.basketIds.join(',');
    }
  }

  async getAlertList(payload: AlertListQuery = {}): Promise<AlertListResponse> {
    const response = await fetcher.post(
      API_ENDPOINTS.ALERT.GET_ALERT_LIST,
      payload,
      undefined,
      ENVIRONMENT_CONFIG.CONSUMER_PRICE_SERVICE
    );
    return unwrapDataResponse<AlertListResponse>(response.data);
  }

  async getAlertMapData(query: { productRefId?: string; timeframe?: string } = {}): Promise<{
    mapData: { [locationKey: string]: unknown };
    totalAlerts: number;
    timeframe: string;
    productRefId?: string;
  }> {
    const params = new URLSearchParams();

    if (query.productRefId) params.append('productRefId', query.productRefId);
    if (query.timeframe) params.append('timeframe', query.timeframe);

    const response = await fetcher.get(
      API_ENDPOINTS.ALERT.GET_ALERT_MAP_DATA,
      params,
      undefined,
      this.baseUrl
    );
    return response.data;
  }

  async getPerformance(query: PerformanceQuery): Promise<PerformanceData> {
    const queryParams: Record<string, string> = {};

    queryParams.startDate = query.startDate.toString();
    queryParams.endDate = query.endDate.toString();
    if (query.userId) queryParams.userId = query.userId.toString();

    const response = await fetcher.get(
      API_ENDPOINTS.PERFORMANCE.GET_PERFORMANCE,
      queryParams,
      undefined,
      this.baseUrl
    );
    return response.data as unknown as PerformanceData;
  }

  async getDetailedPerformance(query: PerformanceQuery): Promise<DetailedPerformanceData> {
    const queryParams: Record<string, string> = {};
    queryParams.page = query.page?.toString() || '1';
    queryParams.limit = query.limit?.toString() || '10';
    queryParams.startDate = query.startDate.toString();
    queryParams.endDate = query.endDate.toString();
    if (query.userId) queryParams.userId = query.userId.toString();

    const response = await fetcher.get(
      API_ENDPOINTS.PERFORMANCE.GET_DETAILED_PERFORMANCE,
      queryParams,
      undefined,
      this.baseUrl
    );
    return response.data as unknown as DetailedPerformanceData;
  }

  uploadExternalData = async (data: ExternalDataUpload): Promise<any> => {
    try {
      const res = await fetcher.post(
        API_ENDPOINTS.EXTERNAL_DATA.POST_EXTERNAL_DATA_UPLOAD,
        data,
        {},
        this.baseUrl
      );
      return res;
    } catch (error) {
      throw error;
    }
  };

  downloadExternalDataTemplate = (data: ExternalDataTemplateDownload) => {
    return fetcher.get(
      API_ENDPOINTS.EXTERNAL_DATA.DOWNLOAD_EXTERNAL_DATA_TEMPLATE,
      data,
      { responseType: 'blob' },
      this.baseUrl
    );
  };

  submitIHMAPricingData = (data: IHMAPricingData): Promise<any> => {
    return fetcher.post(API_ENDPOINTS.EXTERNAL_DATA.SUBMIT_IHMA_PRICING, data, {}, this.baseUrl);
  };

  async getIhmaPriceBreakdown(
    params: IhmaPriceBreakdownParams
  ): Promise<IhmaPriceBreakdownResponse> {
    const queryParams: Record<string, string> = {};

    if (params.productId) queryParams.productId = params.productId.toString();
    if (params.startMonthEpoch) queryParams.startMonthEpoch = params.startMonthEpoch.toString();
    if (params.endMonthEpoch) queryParams.endMonthEpoch = params.endMonthEpoch.toString();
    if (params.timezoneOffset !== undefined)
      queryParams.timezoneOffset = params.timezoneOffset.toString();

    const response = await fetcher.get(
      API_ENDPOINTS.EXTERNAL_DATA.GET_IHMA_PRICE_BREAKDOWN,
      queryParams,
      undefined,
      this.baseUrl
    );
    return response.data as unknown as IhmaPriceBreakdownResponse;
  }

  async getBCHOverlay(params: BCHOverlayParams): Promise<any> {
    const queryParams: Record<string, string> = {};

    if (params?.type) queryParams.type = params.type;
    if (params?.mode) queryParams.mode = params.mode;
    if (params?.year != null) queryParams.year = params.year.toString();
    if (params?.division) queryParams.division = params.division;
    if (params?.region) queryParams.region = params.region;
    if (params?.startTime != null) queryParams.startTime = params.startTime.toString();
    if (params?.endTime != null) queryParams.endTime = params.endTime.toString();
    if (params?.timezoneOffset != null)
      queryParams.timezoneOffset = params.timezoneOffset.toString();

    const response = await fetcher.get(
      API_ENDPOINTS.EXTERNAL_DATA.GET_BCH_OVERLAY,
      queryParams,
      undefined,
      this.baseUrl
    );

    return response.data as unknown as any;
  }

  async getAveragePriceAnalysis(params: {
    basketId?: number;
    locationIds?: number[];
    productIds?: number[];
    startTime?: number;
    endTime?: number;
    timezoneOffset?: number;
  }): Promise<{ averagePrice: number }> {
    const queryParams: Record<string, string | number[]> = {};
    await this.resolveBasketParams(params, queryParams);
    if (params?.startTime !== undefined) queryParams.startTime = params.startTime.toString();
    if (params?.endTime !== undefined) queryParams.endTime = params.endTime.toString();
    if (params?.locationIds && Array.isArray(params.locationIds) && params.locationIds.length > 0)
      queryParams.locationIds = params.locationIds.join(',');
    if (
      params?.productIds &&
      Array.isArray(params.productIds) &&
      params.productIds.length > 0
    )
      queryParams.productIds = params.productIds;
    if (params?.timezoneOffset !== undefined)
      queryParams.timezoneOffset = params.timezoneOffset.toString();

    const response = await fetcher.get(
      API_ENDPOINTS.ANALYSIS.GET_PUBLIC_PRICE_AVERAGE,
      queryParams,
      undefined,
      this.baseUrl
    );
    return unwrapDataResponse<{ averagePrice: number }>(response.data);
  }

  async getPriceChange(params: {
    basketId?: number;
    productIds?: number[];
    locationIds?: number[];
    startTime?: number;
    endTime?: number;
    timezoneOffset?: number;
  }): Promise<{
    currentPeriodAverage: number;
    previousPeriodAverage: number;
    percentageChange: number;
    changeDirection: 'increase' | 'decrease' | 'no-change';
    filters: {
      productIds?: number[];
      basketId?: number;
      locationIds?: number[];
      startTime?: number;
      endTime?: number;
    };
  }> {
    const queryParams: Record<string, string | number[]> = {};
    await this.resolveBasketParams(params, queryParams);
    if (params?.startTime !== undefined) queryParams.startTime = params.startTime.toString();
    if (params?.endTime !== undefined) queryParams.endTime = params.endTime.toString();
    if (params?.locationIds && Array.isArray(params.locationIds) && params.locationIds.length > 0)
      queryParams.locationIds = params.locationIds.join(',');
    if (
      params?.productIds &&
      Array.isArray(params.productIds) &&
      params.productIds.length > 0
    )
      queryParams.productIds = params.productIds;
    if (params?.timezoneOffset !== undefined)
      queryParams.timezoneOffset = params.timezoneOffset.toString();

    const response = await fetcher.get(
      API_ENDPOINTS.ANALYSIS.GET_PRICE_CHANGE,
      queryParams,
      undefined,
      this.baseUrl
    );
    return unwrapDataResponse<{
      currentPeriodAverage: number;
      previousPeriodAverage: number;
      percentageChange: number;
      changeDirection: 'increase' | 'decrease' | 'no-change';
      filters: {
        productIds?: number[];
        basketId?: number;
        locationIds?: number[];
        startTime?: number;
        endTime?: number;
      };
    }>(response.data);
  }

  async getProductsWithPriceChange(
    params: {
      startTime?: number;
      endTime?: number;
      limit?: number;
      establishmentId?: number;
      basketId?: number;
      locationIds?: number[];
      productIds?: number[];
      timezoneOffset?: number;
    },
    type?: 'inflation' | 'deflation'
  ): Promise<any> {
    const queryParams: Record<string, string | number[]> = {};
    await this.resolveBasketParams(params, queryParams);
    if (type) queryParams.type = type;
    if (params?.startTime != null) queryParams.startTime = params.startTime.toString();
    if (params?.endTime != null) queryParams.endTime = params.endTime.toString();
    if (params?.limit !== undefined) queryParams.limit = params.limit.toString();
    if (params?.establishmentId !== undefined && Number.isFinite(params.establishmentId))
      queryParams.establishmentId = params.establishmentId.toString();
    if (params?.locationIds && Array.isArray(params.locationIds) && params.locationIds.length > 0)
      queryParams.locationIds = params.locationIds.join(',');
    if (
      params?.productIds &&
      Array.isArray(params.productIds) &&
      params.productIds.length > 0
    )
      queryParams.productIds = params.productIds;
    if (params?.timezoneOffset !== undefined)
      queryParams.timezoneOffset = params.timezoneOffset.toString();

    const response = await fetcher.get(
      API_ENDPOINTS.ANALYSIS.GET_PUBLIC_PRICE_CHANGE,
      queryParams,
      undefined,
      this.baseUrl
    );
    return unwrapDataResponse<any>(response.data);
  }

  async getPriceBreakdown(params: {
    basketId?: number;
    productIds?: number[];
    locationIds?: number[];
    startTime?: number | null;
    endTime?: number | null;
    timeInterval?: 'daily' | 'weekly' | 'monthly' | 'yearly';
    timezoneOffset?: number;
  }): Promise<{
    categories: string[];
    series: Array<{
      name: string;
      data: number[];
      color?: string;
    }>;
    timeInterval: string;
  }> {
    const queryParams: Record<string, string | number[]> = {};
    await this.resolveBasketParams(params, queryParams);
    if (
      params?.productIds &&
      Array.isArray(params.productIds) &&
      params.productIds.length > 0
    )
      queryParams.productIds = params.productIds;
    if (params?.locationIds && Array.isArray(params.locationIds) && params.locationIds.length > 0)
      queryParams.locationIds = params.locationIds.join(',');
    if (params.startTime != null) queryParams.startTime = params.startTime.toString();
    if (params.endTime != null) queryParams.endTime = params.endTime.toString();
    if (params.timeInterval) queryParams.timeInterval = params.timeInterval;
    if (params.timezoneOffset !== undefined)
      queryParams.timezoneOffset = params.timezoneOffset.toString();

    const response = await fetcher.get(
      API_ENDPOINTS.ANALYSIS.GET_PRICE_BREAKDOWN,
      queryParams,
      undefined,
      this.baseUrl
    );
    return response.data;
  }

  async getBasketAveragePrices(params: {
    locationIds?: number[];
    startTime?: number | null;
    endTime?: number | null;
    timeInterval?: 'daily' | 'weekly' | 'monthly' | 'yearly';
    timezoneOffset?: number;
  }): Promise<{
    categories: string[];
    series: Array<{
      name: string;
      data: number[];
      color?: string;
    }>;
    timeInterval: string;
  }> {
    const queryParams: Record<string, string> = {};
    if (params?.locationIds && Array.isArray(params.locationIds) && params.locationIds.length > 0)
      queryParams.locationIds = params.locationIds.join(',');
    if (params.startTime != null) queryParams.startTime = params.startTime.toString();
    if (params.endTime != null) queryParams.endTime = params.endTime.toString();
    if (params.timeInterval) queryParams.timeInterval = params.timeInterval;
    if (params.timezoneOffset !== undefined)
      queryParams.timezoneOffset = params.timezoneOffset.toString();

    const response = await fetcher.get(
      API_ENDPOINTS.ANALYSIS.GET_BASKET_AVERAGE,
      queryParams,
      undefined,
      this.baseUrl
    );
    return response.data;
  }

  async getProductAveragePrices(params: {
    basketId?: number;
    locationIds?: number[];
    productIds?: number[];
    startTime?: number | null;
    endTime?: number | null;
    timeInterval?: 'daily' | 'weekly' | 'monthly' | 'yearly';
    timezoneOffset?: number;
  }): Promise<{
    categories: string[];
    series: Array<{
      name: string;
      data: number[];
      color?: string;
    }>;
    timeInterval: string;
    timeLabels?: {
      previous: string;
      current: string;
    };
  }> {
    const queryParams: Record<string, string | number[]> = {};
    await this.resolveBasketParams(params, queryParams);
    if (params?.locationIds && Array.isArray(params.locationIds) && params.locationIds.length > 0)
      queryParams.locationIds = params.locationIds.join(',');
    if (
      params?.productIds &&
      Array.isArray(params.productIds) &&
      params.productIds.length > 0
    )
      queryParams.productIds = params.productIds;
    if (params.startTime != null) queryParams.startTime = params.startTime.toString();
    if (params.endTime != null) queryParams.endTime = params.endTime.toString();
    if (params.timeInterval) queryParams.timeInterval = params.timeInterval;
    if (params.timezoneOffset !== undefined)
      queryParams.timezoneOffset = params.timezoneOffset.toString();

    const response = await fetcher.get(
      API_ENDPOINTS.ANALYSIS.GET_PRODUCT_AVERAGE,
      queryParams,
      undefined,
      this.baseUrl
    );
    return response.data;
  }

  async getLocationPriceComparison(params: {
    basketId?: number;
    productIds?: number[];
    timeInterval?: 'daily' | 'weekly' | 'monthly' | 'yearly';
    startTime?: number | null;
    endTime?: number | null;
    timezoneOffset?: number;
  }): Promise<{
    categories: string[];
    series: Array<{
      name: string;
      data: number[];
      color?: string;
    }>;
    timeInterval: string;
  }> {
    const queryParams: Record<string, string | number[]> = {};
    await this.resolveBasketParams(params, queryParams);
    if (
      params?.productIds &&
      Array.isArray(params.productIds) &&
      params.productIds.length > 0
    )
      queryParams.productIds = params.productIds;
    if (params?.timeInterval) queryParams.timeInterval = params.timeInterval;
    if (params.startTime != null) queryParams.startTime = params.startTime.toString();
    if (params.endTime != null) queryParams.endTime = params.endTime.toString();
    if (params.timezoneOffset != null)
      queryParams.timezoneOffset = params.timezoneOffset.toString();

    const response = await fetcher.get(
      API_ENDPOINTS.ANALYSIS.GET_LOCATION_PRICE_COMPARISON,
      queryParams,
      undefined,
      this.baseUrl
    );
    return response.data;
  }

  async getBasketPriceComparison(params: {
    locationIds?: number[];
    timeInterval?: 'daily' | 'weekly' | 'monthly' | 'yearly';
    startTime?: number | null;
    endTime?: number | null;
    timezoneOffset?: number;
  }): Promise<{
    categories: string[];
    series: Array<{
      name: string;
      data: number[];
      color?: string;
    }>;
    timeInterval: string;
  }> {
    const queryParams: Record<string, string> = {};
    if (params?.locationIds && Array.isArray(params.locationIds) && params.locationIds.length > 0)
      queryParams.locationIds = params.locationIds.join(',');
    if (params?.timeInterval) queryParams.timeInterval = params.timeInterval;
    if (params.startTime != null) queryParams.startTime = params.startTime.toString();
    if (params.endTime != null) queryParams.endTime = params.endTime.toString();
    if (params.timezoneOffset != null)
      queryParams.timezoneOffset = params.timezoneOffset.toString();

    const response = await fetcher.get(
      API_ENDPOINTS.ANALYSIS.GET_BASKET_PRICE_COMPARISON,
      queryParams,
      undefined,
      this.baseUrl
    );
    return response.data;
  }

  async getEstablishmentPriceComparison(params: {
    basketId?: number;
    productIds?: number[];
    timeInterval?: 'daily' | 'weekly' | 'monthly' | 'yearly';
    startTime?: number | null;
    endTime?: number | null;
    timezoneOffset?: number;
  }): Promise<{
    categories: string[];
    series: Array<{
      name: string;
      data: number[];
      color?: string;
    }>;
    timeInterval: string;
  }> {
    const queryParams: Record<string, string | number[]> = {};
    await this.resolveBasketParams(params, queryParams);
    if (
      params?.productIds &&
      Array.isArray(params.productIds) &&
      params.productIds.length > 0
    )
      queryParams.productIds = params.productIds;
    if (params?.timeInterval) queryParams.timeInterval = params.timeInterval;
    if (params.startTime != null) queryParams.startTime = params.startTime.toString();
    if (params.endTime != null) queryParams.endTime = params.endTime.toString();
    if (params.timezoneOffset != null)
      queryParams.timezoneOffset = params.timezoneOffset.toString();

    const response = await fetcher.get(
      API_ENDPOINTS.ANALYSIS.GET_ESTABLISHMENT_PRICE_COMPARISON,
      queryParams,
      undefined,
      this.baseUrl
    );
    return response.data;
  }

  async saveUtilityConfig(payload: SaveUtilityConfigPayload): Promise<any> {
    const response = await fetcher.post(
      API_ENDPOINTS.SIMPAH.UPSERT,
      payload,
      undefined,
      this.baseUrl
    );
    return response.data;
  }

  async getUtilityConfigWithProfits(productRefId: string, presentations?: string[]): Promise<any> {
    const params = new URLSearchParams();
    params.append('productRefId', productRefId);
    if (presentations && presentations.length > 0) {
      params.append('presentations', presentations.join(','));
    }
    const response = await fetcher.get(
      API_ENDPOINTS.SIMPAH.GET_PRODUCT_PROFIT,
      params,
      undefined,
      this.baseUrl
    );
    return response.data;
  }
}

export const analysisService = new AnalysisService();
